import React, { useRef, useState, useEffect, Fragment, useMemo } from 'react';
import { head, toNumber, last, compact, isEmpty, sortBy } from 'lodash-es';
import T from 'prop-types';
import Slider from 'react-slick';

import SlidesModal from './SlidesModal';
import Button from '../../../../../components/Buttons/Button';
import Skeleton from '../../../../../components/Skeleton';
import LoadingOverlay from '../../../../../components/LoadingOverlay';

const squareAspectRatio = 1;

const initConfig = {
  arrows: false,
  dots: false,
  speed: 300,
  centerPadding: 0,
  autoplay: false,
  autoplaySpeed: 3000,
  infinite: true,
  accessibility: true,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const Screenshots = ({ screenshots, pending }) => {
  const controlsRef = useRef({});
  const sliderModalRef = useRef({});
  const pendingPositionRef = useRef();
  const [position, setPosition] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const sliderRef = useRef();
  const [aspectRatiosScreenshotsList, setAspectRatiosScreenshotsList] = useState([]);
  const [aspectRatiosScreenshots, setAspectRatiosScreenshots] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAspectRatioFromUrl = (url, index) => {
    if (url.length < 1) {
      setImagesLoaded((prev) => !prev);
    }

    const img = new Image();
    // eslint-disable-next-line
    img.onload = function () {
      const result = {
        index,
        url: screenshots[index],
        ratio: toNumber(this.height) / toNumber(this.width),
      };

      setAspectRatiosScreenshotsList((prev) => [...prev, result]);
      setImagesLoaded((prev) => !prev);
      return result;
    };
    img.src = url;
  };

  useEffect(() => {
    if (screenshots.length && !pending) {
      setAspectRatiosScreenshotsList([]);
      screenshots.map((screen, index) => getAspectRatioFromUrl(screen, index));
      setPosition(0);
      if (sliderRef.current) sliderRef.current.slickGoTo(0);
    }
  }, [screenshots]);

  useEffect(() => {
    setAspectRatiosScreenshots(sortBy(aspectRatiosScreenshotsList, 'index'));
  }, [screenshots, aspectRatiosScreenshotsList.length]);

  const chunkedScreenshots = compact(
    screenshots.reduce((memo, current, index) => {
      const present = !isEmpty(aspectRatiosScreenshots[index]);
      // eslint-disable-next-line
      const perChunk =
        present && aspectRatiosScreenshots[index].ratio > squareAspectRatio
          ? aspectRatiosScreenshots[index].ratio.toFixed(3) === (4 / 3).toFixed(3)
            ? 2
            : 3
          : 1;
      const chunk = Math.floor(index / perChunk);

      memo[chunk] = [].concat(memo[chunk] || [], {
        index,
        url: screenshots[index],
        ratio: present ? aspectRatiosScreenshots[index].ratio : 1,
      });
      return memo;
    }, [])
  );

  useEffect(() => {
    if (pendingPositionRef.current && screenshots[pendingPositionRef.current]) {
      setPosition(pendingPositionRef.current);

      // Reset pendingPosition
      pendingPositionRef.current = undefined;
    }
  }, [screenshots, imagesLoaded]);

  const activeChunk = useMemo(
    () =>
      chunkedScreenshots &&
      chunkedScreenshots.find((shot) => shot && shot.some((item) => item.index === position)),
    [position]
  );

  const handleBeforeMainChange = (currentSlide) => {
    const nextPosition =
      chunkedScreenshots[currentSlide] && chunkedScreenshots[currentSlide][0].index;
    setPosition(nextPosition);
  };

  const handleSliderPrevClick = () => {
    sliderRef.current.slickPrev();
  };

  const handleSliderNextClick = () => {
    sliderRef.current.slickNext();
  };

  const handleModalPrevClick = () => {
    sliderModalRef.current.slickPrev();
  };

  const handleModalNextClick = () => {
    sliderModalRef.current.slickNext();
  };
  const handleBeforeChange = (current, nextSlide) => {
    setPosition(nextSlide);
  };
  const handleModalClose = () => {
    const nextPosition = chunkedScreenshots.findIndex(
      (chunk) => chunk && chunk.some((item) => item.index === position)
    );
    setPosition(nextPosition);
    sliderRef.current.slickGoTo(nextPosition);
    setModalOpen(false);
  };

  const handleImageClick = (slide) => {
    setPosition(slide.index);
    setModalOpen(true);
  };

  const displayPosition = useMemo(
    () =>
      activeChunk && activeChunk.length > 1
        ? `${head(activeChunk).index + 1}-${last(activeChunk).index + 1}`
        : `${position + 1}`,
    [activeChunk, position]
  );

  useEffect(() => {
    if (modalOpen && sliderModalRef.current) {
      setLoading(true);
      sliderModalRef.current.slickGoTo(position);
    }
  }, [modalOpen]);

  // Keep reference for optimization purposes
  controlsRef.current.prev = handleSliderPrevClick;
  controlsRef.current.next = handleSliderNextClick;

  useEffect(() => {
    const handleKeydown = ({ key }) => {
      const direction = {
        ArrowLeft: 'prev',
        ArrowRight: 'next',
      }[key];

      if (!direction) return;
      if (modalOpen) {
        direction === 'next' ? handleModalNextClick() : handleModalPrevClick();
      } else controlsRef.current[direction]();
    };
    document.addEventListener('keydown', handleKeydown);
    if (modalOpen) {
      return () => document.removeEventListener('keydown', handleKeydown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [modalOpen]);

  if (pending) return null;

  const slides = chunkedScreenshots.map((slide) => {
    if (slide.length === 1 && slide[0].ratio <= squareAspectRatio) {
      return (
        <div key={slide[0].index} className="insights-details-block__slide">
          <img src={slide[0].url} alt="screenshot" onClick={() => handleImageClick(slide[0])} />
          <Skeleton height={230} borderRadius={12} />
        </div>
      );
    }
    if (slide.length === 2 && slide[0].ratio.toFixed(3) === (4 / 3).toFixed(3)) {
      return (
        <div key={slide} className="insights-details-block__slide">
          {slide.map((slidChild) => (
            <div key={slidChild.index} className="insights-details-block__slide-double">
              <img
                src={slidChild.url}
                alt="screenshot"
                onClick={() => handleImageClick(slidChild)}
              />
              <Skeleton height={345} borderRadius={12} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div key={slide} className="insights-details-block__slide">
        {slide.map((slidChild) => (
          <div key={slidChild.index} className="insights-details-block__slide-triple">
            <img src={slidChild.url} alt="screenshot" onClick={() => handleImageClick(slidChild)} />
            <Skeleton height={345} borderRadius={12} />
          </div>
        ))}
      </div>
    );
  });

  return (
    <Fragment>
      <Slider {...initConfig} ref={sliderRef} afterChange={handleBeforeMainChange}>
        {slides}
      </Slider>

      <div className="slider-pagination">
        <div className="slider-pagination-current">
          {displayPosition} of {screenshots.length}
        </div>
        <div className="slider-pagination-buttons">
          <Button
            shape="square"
            size="sm"
            variant="secondary"
            className="prev-button"
            onClick={handleSliderPrevClick}
          >
            <span className="hicon-angle-left-new" />
          </Button>
          <Button
            shape="square"
            size="sm"
            variant="secondary"
            className="next-button"
            onClick={handleSliderNextClick}
          >
            <span className="hicon-angle-right-new" />
          </Button>
        </div>
      </div>
      <Fragment>
        {loading && <LoadingOverlay loading={loading} className="zIndex-1500 bg-transparent" />}
        <SlidesModal
          ref={sliderModalRef}
          open={aspectRatiosScreenshots && modalOpen}
          position={position}
          loading={loading}
          screenshots={compact(aspectRatiosScreenshots)}
          onChange={handleBeforeChange}
          onAllLoaded={() => setLoading(false)}
          onClose={handleModalClose}
        />
      </Fragment>
    </Fragment>
  );
};

Screenshots.propTypes = {
  screenshots: T.array,
  pending: T.bool,
};

export default Screenshots;
